/**
 * MirrorDisplayPanel — Mission Control card for configuring how
 * Mirror renders performance numbers for this organization. Lives
 * inside OrgSettingsPanel as a separate tab so the toggle and
 * threshold editor stay together but don't crowd the Profile /
 * Financials surfaces.
 *
 * Persists via PUT /api/personal/display-mode/{org_id}.
 * Resets via the "Reset to defaults" button (sends mode=numbers +
 * the DEFAULT_THRESHOLDS payload).
 */
import secureStorage from '../../lib/secureStorage';
import React, { useState, useEffect } from 'react';
import authedFetch from '../../lib/authedFetch';
import {
  Eye, EyeOff, Plus, Trash2, RefreshCcw, Loader2, Save, Info, MessageSquareText,
  TrendingUp, ArrowRight, Sparkles, Brain, X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import logger from '../../lib/logger';
import { valueToBucket, deltaToTrendLabel } from '../../pages/personal-impact/PIEDisplayMode';
import useIndustryProfilesList from '../../hooks/useIndustryProfilesList';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const DEFAULT_THRESHOLDS = [
  { min: 0, max: 39, label: 'Needs Focus' },
  { min: 40, max: 59, label: 'Building' },
  { min: 60, max: 79, label: 'On Track' },
  { min: 80, max: 100, label: 'Excelling' },
];

const DEFAULT_TREND_LABELS = {
  improving: 'Improving',
  steady: 'Steady',
  slipping: 'Slipping',
};

export default function MirrorDisplayPanel({ orgId }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('numbers');
  const [thresholds, setThresholds] = useState(DEFAULT_THRESHOLDS);
  const [trendLabels, setTrendLabels] = useState(DEFAULT_TREND_LABELS);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  // P2 — let admins layer an industry context onto the LLM preview to
  // see how the chosen vertical's tone shifts the narrative without
  // having to re-tag the org first.
  const [previewIndustryId, setPreviewIndustryId] = useState('');
  const { industries: industryProfiles } = useIndustryProfilesList();

  useEffect(() => {
    if (!orgId) return;
    const fetchConfig = async () => {
      setLoading(true);
      try {
        const token = secureStorage.getItem('session_token');
        const res = await authedFetch(
          `${API_URL}/api/personal/display-mode?org_id=${orgId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.ok) {
          const data = await res.json();
          setMode(data.mode === 'verbiage' ? 'verbiage' : 'numbers');
          if (Array.isArray(data.thresholds) && data.thresholds.length) {
            setThresholds(data.thresholds);
          }
          if (data.trend_labels) {
            setTrendLabels({ ...DEFAULT_TREND_LABELS, ...data.trend_labels });
          }
        }
      } catch (e) {
        logger.error('Failed to load display mode', e);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [orgId, API_URL]);

  const updateTier = (idx, field, value) => {
    setThresholds((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: field === 'label' ? value : Number(value) };
      return next;
    });
  };

  const addTier = () => {
    setThresholds((prev) => [
      ...prev,
      { min: 0, max: 100, label: `Tier ${prev.length + 1}` },
    ]);
  };

  const removeTier = (idx) => {
    setThresholds((prev) => prev.filter((_, i) => i !== idx));
  };

  const sortTiers = () => {
    setThresholds((prev) => [...prev].sort((a, b) => a.min - b.min));
  };

  const handleSave = async () => {
    if (!orgId) return;
    // Validate: each tier must have a label and min <= max, range 0..100.
    for (const t of thresholds) {
      if (!t.label || !t.label.trim()) {
        toast.error('Every tier needs a label');
        return;
      }
      if (t.min < 0 || t.max > 100 || t.min > t.max) {
        toast.error(`Invalid range for "${t.label}": min must be ≤ max, 0–100`);
        return;
      }
    }
    setSaving(true);
    try {
      const token = secureStorage.getItem('session_token');
      const res = await authedFetch(`${API_URL}/api/personal/display-mode/${orgId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ mode, thresholds, trend_labels: trendLabels }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Mirror display settings saved');
        if (Array.isArray(data.thresholds)) setThresholds(data.thresholds);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save display settings');
      }
    } catch (e) {
      logger.error('Save display mode failed', e);
      toast.error('Failed to save display settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setMode('numbers');
    setThresholds(DEFAULT_THRESHOLDS);
    setTrendLabels(DEFAULT_TREND_LABELS);
    toast.message('Defaults applied. Click Save to persist.');
  };

  const handleSampleAINarrative = async () => {
    // Validate first so the LLM doesn't choke on garbage tier rows.
    for (const t of thresholds) {
      if (!t.label || !t.label.trim()) {
        toast.error('Every tier needs a label before previewing');
        return;
      }
      if (t.min < 0 || t.max > 100 || t.min > t.max) {
        toast.error(`Invalid range for "${t.label}": min must be ≤ max, 0–100`);
        return;
      }
    }
    setPreviewing(true);
    setPreviewResult(null);
    setPreviewOpen(true);
    try {
      const token = secureStorage.getItem('session_token');
      const res = await authedFetch(`${API_URL}/api/personal/display-mode/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          mode,
          thresholds,
          trend_labels: trendLabels,
          industry_id: previewIndustryId || null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setPreviewResult(data);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to generate preview');
        setPreviewOpen(false);
      }
    } catch (e) {
      logger.error('Preview AI call failed', e);
      toast.error('Failed to generate preview');
      setPreviewOpen(false);
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading display settings…
        </CardContent>
      </Card>
    );
  }

  const verbiageOn = mode === 'verbiage';

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquareText className="h-5 w-5 text-teal-600" /> Mirror display mode
          </CardTitle>
          <CardDescription>
            Choose how performance values appear to learners and managers in Mirror.
            Mission Control always shows raw numbers.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div
            className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4"
            data-testid="mirror-display-mode-toggle-row"
          >
            <div className="flex items-start gap-3">
              {verbiageOn ? (
                <EyeOff className="h-5 w-5 text-teal-600 mt-0.5" />
              ) : (
                <Eye className="h-5 w-5 text-slate-500 mt-0.5" />
              )}
              <div>
                <Label className="text-sm font-medium text-slate-800">
                  Hide performance numbers in Mirror
                </Label>
                <p className="text-xs text-slate-500 mt-1 max-w-md">
                  When on, raw percentages and pulse scores are replaced with the
                  descriptive tiers below (e.g. &ldquo;On Track&rdquo;). Trends keep their
                  direction but read as &ldquo;Improving&rdquo; instead of &ldquo;+2.3 pts&rdquo;.
                </p>
              </div>
            </div>
            <Switch
              checked={verbiageOn}
              onCheckedChange={(v) => setMode(v ? 'verbiage' : 'numbers')}
              data-testid="mirror-display-mode-switch"
            />
          </div>

          <MirrorPreview mode={mode} thresholds={thresholds} trendLabels={trendLabels} />

          <div className="space-y-3" data-testid="mirror-display-thresholds">
            <div className="flex items-center justify-between gap-2">
              <div>
                <Label className="text-sm font-medium text-slate-800">
                  Verbiage tiers
                </Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Map a 0–100 range to a label. Tiers are evaluated low → high; the
                  first matching tier wins. Pulse scores (1–5) are normalised to
                  the same scale automatically.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={sortTiers}
                  data-testid="mirror-display-sort-tiers"
                >
                  Sort
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addTier}
                  data-testid="mirror-display-add-tier"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add tier
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-[110px_110px_1fr_40px] gap-2 text-[11px] uppercase tracking-wide text-slate-400 px-1">
              <div>Min</div>
              <div>Max</div>
              <div>Label</div>
              <div />
            </div>

            <div className="space-y-2">
              {thresholds.map((t, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-[110px_110px_1fr_40px] gap-2 items-center"
                  data-testid={`mirror-display-tier-row-${idx}`}
                >
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={t.min}
                    onChange={(e) => updateTier(idx, 'min', e.target.value)}
                    data-testid={`mirror-display-tier-min-${idx}`}
                  />
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={t.max}
                    onChange={(e) => updateTier(idx, 'max', e.target.value)}
                    data-testid={`mirror-display-tier-max-${idx}`}
                  />
                  <Input
                    type="text"
                    value={t.label}
                    onChange={(e) => updateTier(idx, 'label', e.target.value)}
                    placeholder="e.g. On Track"
                    data-testid={`mirror-display-tier-label-${idx}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeTier(idx)}
                    disabled={thresholds.length <= 1}
                    className="text-rose-500 hover:text-rose-600 hover:bg-rose-50"
                    data-testid={`mirror-display-tier-remove-${idx}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3 pt-2" data-testid="mirror-display-trend-labels">
            <div>
              <Label className="text-sm font-medium text-slate-800">
                Trend labels
              </Label>
              <p className="text-xs text-slate-500 mt-0.5">
                How week-over-week deltas read in verbiage mode. Magnitudes are
                still calculated; only the wording changes.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-emerald-700">Up</Label>
                <Input
                  value={trendLabels.improving}
                  onChange={(e) =>
                    setTrendLabels((p) => ({ ...p, improving: e.target.value }))
                  }
                  data-testid="mirror-display-trend-improving"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Flat</Label>
                <Input
                  value={trendLabels.steady}
                  onChange={(e) =>
                    setTrendLabels((p) => ({ ...p, steady: e.target.value }))
                  }
                  data-testid="mirror-display-trend-steady"
                />
              </div>
              <div>
                <Label className="text-xs text-rose-700">Down</Label>
                <Input
                  value={trendLabels.slipping}
                  onChange={(e) =>
                    setTrendLabels((p) => ({ ...p, slipping: e.target.value }))
                  }
                  data-testid="mirror-display-trend-slipping"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t">
            <div className="text-xs text-slate-500 flex items-start gap-2">
              <Info className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
              <span>
                Changes apply to every Mirror tab opened after Save. Active
                Mirror sessions refresh on next page load.
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2 justify-end">
              <div className="flex items-center gap-1.5 mr-1" data-testid="mirror-display-industry-picker">
                <Sparkles className="h-3 w-3 text-violet-500" />
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-0">
                  Industry tone
                </Label>
                <Select value={previewIndustryId || 'none'} onValueChange={(v) => setPreviewIndustryId(v === 'none' ? '' : v)}>
                  <SelectTrigger className="h-8 w-[170px] text-xs border-violet-200" data-testid="mirror-display-industry-trigger">
                    <SelectValue placeholder="No industry overlay" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" data-testid="mirror-display-industry-none">— No industry overlay</SelectItem>
                    {industryProfiles.map((ind) => (
                      <SelectItem key={ind.id} value={ind.id} data-testid={`mirror-display-industry-${ind.id}`}>
                        {ind.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSampleAINarrative}
                disabled={previewing}
                data-testid="mirror-display-sample-ai-btn"
                className="border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
              >
                {previewing ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                ) : (
                  <Brain className="h-3.5 w-3.5 mr-1" />
                )}
                Sample AI narrative
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleReset}
                data-testid="mirror-display-reset-btn"
              >
                <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Reset to defaults
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="mirror-display-save-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save display settings
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {previewOpen && (
        <SampleNarrativeModal
          mode={mode}
          previewing={previewing}
          result={previewResult}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * SampleNarrativeModal — shows the actual Coach + Insights LLM output
 * for the chosen draft tier labels, side-by-side. Closes the loop
 * between "I picked these labels" and "what will my learners actually
 * read?" without forcing an org admin to flip the switch first.
 */
function SampleNarrativeModal({ mode, previewing, result, onClose }) {
  const verbiage = mode === 'verbiage';
  return (
    <div
      role="presentation"
      onClick={onClose}
      data-testid="mirror-display-sample-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 60,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        role="dialog"
        aria-labelledby="mirror-sample-title"
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl"
        style={{
          width: 'min(960px, 100%)',
          maxHeight: '88vh',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b">
          <div className="flex items-center gap-2 flex-wrap">
            <Brain className="h-5 w-5 text-violet-600" />
            <h3 id="mirror-sample-title" className="text-base font-semibold text-slate-900">
              Sample AI narrative
            </h3>
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 ml-2">
              {verbiage ? 'Verbiage mode' : 'Numbers mode'}
            </span>
            {result?.industry_context?.applied && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 border border-violet-200 text-violet-700"
                data-testid="mirror-display-sample-industry-chip"
                title="Industry context layered onto the system prompt for this preview"
              >
                <Sparkles className="h-2.5 w-2.5" />
                {result.industry_context.industry_label}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-slate-100"
            data-testid="mirror-display-sample-close"
            aria-label="Close"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {previewing && (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 py-12 text-slate-500">
            <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
            <div className="text-sm">Asking Claude with these tier labels…</div>
            <div className="text-xs text-slate-400">One Coach + one Insights round-trip · ~6–10s</div>
          </div>
        )}

        {!previewing && result && (
          <div className="flex-1 overflow-y-auto p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <NarrativeBlock
              title="What the AI Coach says"
              subtitle={`To: ${result.persona?.name || 'persona'} · ${result.persona?.archetype_label || ''}`}
              body={result.coach?.response || '(no response)'}
              tone="teal"
              testid="mirror-display-sample-coach"
            />
            <NarrativeBlock
              title="What the Insights engine says"
              subtitle="Diagnosis + top priority"
              body={[
                result.insights?.narrative?.diagnosis,
                '',
                result.insights?.narrative?.top_priority_why,
                '',
                ...(result.insights?.top3 || []).slice(0, 3).map((o) => {
                  const blurb = (result.insights?.narrative?.blurbs || {})[o.id];
                  return `• ${o.label}${blurb ? ` — ${blurb}` : ''}`;
                }),
              ].filter((x) => x !== undefined).join('\n')}
              tone="violet"
              testid="mirror-display-sample-insights"
            />
          </div>
        )}

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t bg-slate-50">
          <div className="text-[11px] text-slate-500">
            {result?.persona ? (
              <>
                Sampled against <strong className="text-slate-700">{result.persona.name}</strong> ({result.persona.role_label}) — synthetic showcase persona.
              </>
            ) : (
              'Live LLM round-trip against a synthetic persona.'
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            data-testid="mirror-display-sample-done"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

function NarrativeBlock({ title, subtitle, body, tone, testid }) {
  const toneCfg = {
    teal: {
      headerBg: 'bg-teal-50',
      headerText: 'text-teal-800',
      border: 'border-teal-100',
    },
    violet: {
      headerBg: 'bg-violet-50',
      headerText: 'text-violet-800',
      border: 'border-violet-100',
    },
  }[tone || 'teal'];
  return (
    <div className={`rounded-xl border ${toneCfg.border} overflow-hidden flex flex-col bg-white`} data-testid={testid}>
      <div className={`px-4 py-2.5 ${toneCfg.headerBg}`}>
        <div className={`text-xs font-semibold ${toneCfg.headerText}`}>{title}</div>
        {subtitle && <div className="text-[10px] text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      <div
        className="p-4 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap overflow-y-auto"
        style={{ maxHeight: '52vh' }}
      >
        {body}
      </div>
    </div>
  );
}

/**
 * MirrorPreview — live preview pane that mirrors how learners will see
 * their PE hero, triad cards and a sample delta in Mirror. Re-renders
 * on every change to mode / thresholds / trend labels so org admins
 * can dial in their copy without an account switch.
 *
 * Uses fixed sample values that are deliberately spread across the
 * tier range (PE 72%, Capability 86%, Motivation 54%, LEI 38%, ΔPE
 * +2.5 pts) so editing any threshold immediately shifts which sample
 * card lands in which tier — fast feedback for "did I pick sensible
 * cutoffs?".
 */
function MirrorPreview({ mode, thresholds, trendLabels }) {
  const verbiage = mode === 'verbiage';

  const SAMPLE = {
    pe: 0.72,
    capability: 0.86,
    motivation: 0.54,
    lei: 0.38,
    delta: 0.025,
  };

  const renderValue = (val, kind = 'pct01') => {
    if (verbiage) {
      return valueToBucket(val, thresholds, kind) || '—';
    }
    return `${Math.round(val * 100)}%`;
  };

  const renderDelta = (val) => {
    if (verbiage) return deltaToTrendLabel(val, trendLabels, 'pct01') || '—';
    const pp = val * 100;
    const sign = pp >= 0 ? '+' : '';
    return `${sign}${pp.toFixed(1)} pts`;
  };

  const peLabel = renderValue(SAMPLE.pe);

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white overflow-hidden"
      data-testid="mirror-display-preview"
    >
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
          <Sparkles className="h-3.5 w-3.5 text-teal-600" />
          Preview · how a learner will see this
        </div>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">
          Sample data · {verbiage ? 'Verbiage mode' : 'Numbers mode'}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_minmax(200px,260px)] gap-5 p-5 bg-gradient-to-br from-slate-50 to-white">
        {/* PE hero */}
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-teal-600 flex items-center gap-1.5">
            <TrendingUp className="h-3 w-3" /> Personal Effectiveness
          </div>
          <div className="flex items-baseline gap-3 mt-2 flex-wrap">
            <span
              className={`font-bold text-slate-900 ${verbiage ? 'text-3xl leading-tight' : 'text-5xl'}`}
              data-testid="mirror-display-preview-pe"
              style={{ wordBreak: 'break-word' }}
            >
              {peLabel}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 text-[11px] font-semibold border border-teal-100">
              {renderDelta(SAMPLE.delta)}
            </span>
          </div>
          <p className="mt-3 text-xs text-slate-500 leading-relaxed max-w-sm">
            {verbiage
              ? 'Room to grow — closing it usually comes from the Motivation side.'
              : '22pp of headroom — that\u2019s the gap between where you are and full drive.'}
          </p>
        </div>

        {/* Triad mini-cards */}
        <div className="flex flex-col gap-2 md:border-l md:pl-5 md:border-slate-200">
          <PreviewRow label="Capability" value={renderValue(SAMPLE.capability)} pct={SAMPLE.capability * 100} testid="mirror-display-preview-capability" />
          <PreviewRow label="Motivation" value={renderValue(SAMPLE.motivation)} pct={SAMPLE.motivation * 100} testid="mirror-display-preview-motivation" />
          <PreviewRow label="Personal LEI" value={renderValue(SAMPLE.lei)} pct={SAMPLE.lei * 100} testid="mirror-display-preview-lei" />
        </div>
      </div>

      {/* Heatmap row preview */}
      <div className="px-5 pb-5">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
          Manager heatmap sample
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { v: 0.82, key: 'cap' },
            { v: 0.55, key: 'mot' },
            { v: 0.31, key: 'beh' },
          ].map((c) => {
            const bucket = c.v >= 0.7 ? 'strong' : c.v >= 0.5 ? 'moderate' : c.v >= 0.3 ? 'constrained' : 'critical';
            const colours = {
              strong:      { bg: 'rgba(16, 185, 129, 0.16)', fg: '#047857', bd: 'rgba(16,185,129,0.4)' },
              moderate:    { bg: 'rgba(6, 182, 212, 0.16)',  fg: '#0E7490', bd: 'rgba(6,182,212,0.4)' },
              constrained: { bg: 'rgba(245, 158, 11, 0.16)', fg: '#B45309', bd: 'rgba(245,158,11,0.4)' },
              critical:    { bg: 'rgba(239, 68, 68, 0.16)',  fg: '#B91C1C', bd: 'rgba(239,68,68,0.4)' },
            }[bucket];
            return (
              <div
                key={c.key}
                style={{
                  background: colours.bg,
                  color: colours.fg,
                  border: `1px solid ${colours.bd}`,
                  borderRadius: 8,
                  fontSize: verbiage ? 11 : 13,
                  fontWeight: 600,
                  padding: '8px 10px',
                  textAlign: 'center',
                }}
                data-testid={`mirror-display-preview-heatmap-${c.key}`}
              >
                {renderValue(c.v)}
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-slate-400 mt-2 text-right">
          Mission Control admins (you) always see raw numbers regardless of this setting.
        </p>
      </div>
    </div>
  );
}

function PreviewRow({ label, value, pct, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between gap-2 text-[10px] uppercase tracking-wide font-semibold text-slate-500">
        <span>{label}</span>
        <span className="text-sm font-bold text-slate-900 normal-case tracking-normal" style={{ wordBreak: 'break-word', textAlign: 'right' }}>
          {value}
        </span>
      </div>
      <div className="h-1 mt-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-teal-500"
          style={{ width: `${Math.max(0, Math.min(100, pct))}%`, transition: 'width 300ms' }}
        />
      </div>
    </div>
  );
}
